import {
  AlarmCallType,
  AlarmLogActionCallKind,
  AlarmLogEvent,
  AlarmLogEventKind,
  AlarmLogProfessionalResponderKind,
} from '@x-guard/xgac-types/xgac';
import {
  mdiAccountCheck,
  mdiAlarmLight,
  mdiBell,
  mdiChat,
  mdiEmail,
  mdiInformation,
  mdiMerge,
  mdiPhone,
  mdiProtocol,
} from '@mdi/js';
import { TranslationFunc } from 'config/i18n';

const actionCallKindKey = (kind: AlarmLogActionCallKind): 'initiated' | 'cancelled' | 'notReachable' | 'noResponse' | 'responding' => {

  switch (kind) {

  case AlarmLogActionCallKind.ResultCancelled:
    return 'cancelled';
  case AlarmLogActionCallKind.ResultNotReachable:
    return 'notReachable';
  case AlarmLogActionCallKind.ResultNoResponse:
    return 'noResponse';
  case AlarmLogActionCallKind.ResultResponding:
    return 'responding';
  default:
    return 'initiated';

  }

};

export const alarmLogIcon = (event: AlarmLogEvent): string => {

  // Added by the log screen itself: not an event kind of the API.
  if ((event.data?.eventType as string) === 'alarmCreated') return mdiAlarmLight;

  switch (event.data?.eventType) {

  case AlarmLogEventKind.Chat:
  case AlarmLogEventKind.OperatorChat:
  case AlarmLogEventKind.Sms:
    return mdiChat;
  case AlarmLogEventKind.Email:
    return mdiEmail;
  case AlarmLogEventKind.Notification:
  case AlarmLogEventKind.NotificationConfirmedDelivery:
  case AlarmLogEventKind.ManualNotify:
    return mdiBell;
  case AlarmLogEventKind.ActionCall:
  case AlarmLogEventKind.ActionCallStaticResponder:
  case AlarmLogEventKind.ActionCallToStaticResponder:
  case AlarmLogEventKind.ActionCallToDynamicResponder:
  case AlarmLogEventKind.CallSession:
  case AlarmLogEventKind.CallMeta:
    return mdiPhone;
  case AlarmLogEventKind.Acknowledge:
  case AlarmLogEventKind.ResponseConfirmation:
    return mdiAccountCheck;
  case AlarmLogEventKind.AlarmMerged:
    return mdiMerge;
  case AlarmLogEventKind.Protocol:
    return mdiProtocol;
  default:
    return mdiInformation;

  }

};

const isAnonymousOperator = (createdBy: AlarmLogEvent['createdBy']): boolean => (
  createdBy?._ref === 'User' && !createdBy.name?.trim() && !('isServiceAccount' in createdBy && createdBy.isServiceAccount)
);

export const operatorDisplayNames = (
  events: AlarmLogEvent[],
  singular: string,
  numbered: (number: number) => string,
): Map<string, string> => {

  const ids: string[] = [];
  const seen = new Set<string>();

  [...events]
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
    .forEach((event) => {

      if (!isAnonymousOperator(event.createdBy) || !event.createdBy || seen.has(event.createdBy._id)) return;

      seen.add(event.createdBy._id);
      ids.push(event.createdBy._id);

    });

  const names = new Map<string, string>();

  if (ids.length === 1) {

    names.set(ids[0], singular);
    return names;

  }

  ids.forEach((id, index) => names.set(id, numbered(index + 1)));

  return names;

};

export const alarmLogActor = (event: AlarmLogEvent, t: TranslationFunc, operatorNames?: Map<string, string>): string => {

  const createdBy = event.createdBy;

  if (!createdBy || ('isServiceAccount' in createdBy && createdBy.isServiceAccount)) {

    return t('Alarm_Detail_Logs_System');

  }

  if (isAnonymousOperator(createdBy)) {

    return operatorNames?.get(createdBy._id) ?? t('Alarm_Detail_Logs_Operator');

  }

  return createdBy.name;

};

/** "someNewKind" -> "Some new kind": an English best effort for events this screen does not know yet. */
const humanize = (eventType: string): string => {

  const words = eventType.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim().toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);

};

// The API stores the text of this system chat message, not its translation key.
export const isEscalatedToAlarmCenter = (data: AlarmLogEvent['data']): boolean => (
  data?.eventType === AlarmLogEventKind.Chat && /^alarm has been escalated to alarm center\.?$/i.test(data.message.trim())
);

export const alarmLogTitle = (event: AlarmLogEvent, t: TranslationFunc): string => {

  const { data } = event;

  if (!data) return t('Alarm_Detail_Logs_Unknown');

  // Declared in the event kinds but without a data shape in the types, so it cannot be a switch case.
  if ((data.eventType as string) === 'actionCallDynamicResponder') {

    const call = data as unknown as { kind?: AlarmLogActionCallKind; name?: string };

    return t(`Alarm_Detail_Logs_ActionCall_${actionCallKindKey(call.kind ?? AlarmLogActionCallKind.Initiated)}`, {
      name: call.name ?? t('Alarm_Detail_Logs_Unknown'),
    });

  }

  if ((data.eventType as string) === 'alarmCreated') return t('Alarm_Detail_Logs_Created');

  if (isEscalatedToAlarmCenter(data)) return t('Alarm_Detail_Logs_Escalated');

  switch (data.eventType) {

  case AlarmLogEventKind.Chat:
    return t('Alarm_Detail_Logs_Chat');
  case AlarmLogEventKind.Notification:
    return t('Alarm_Detail_Logs_Notification', { name: data.to.name });
  case AlarmLogEventKind.NotificationConfirmedDelivery:
    return t('Alarm_Detail_Logs_NotificationConfirmed', { name: data.to.name });
  case AlarmLogEventKind.AlarmViewedInApp:
    return t('Alarm_Detail_Logs_Viewed', { name: data.by.name });
  case AlarmLogEventKind.ManualNotify:
    return t('Alarm_Detail_Logs_ManualNotify');
  case AlarmLogEventKind.Sms:
    return t('Alarm_Detail_Logs_Sms', { name: data.name });
  case AlarmLogEventKind.Email:
    return t('Alarm_Detail_Logs_Email', { name: data.name });
  case AlarmLogEventKind.ActionCall:
  case AlarmLogEventKind.ActionCallStaticResponder:
  case AlarmLogEventKind.ActionCallToStaticResponder:
  case AlarmLogEventKind.ActionCallToDynamicResponder:
    return t(`Alarm_Detail_Logs_ActionCall_${actionCallKindKey(data.kind)}`, { name: data.name });
  case AlarmLogEventKind.CallSession:
    return t(`Alarm_Detail_Logs_CallSession_${data.phase}`);
  case AlarmLogEventKind.ResponseConfirmation:
    return t(data.value ? 'Alarm_Detail_Logs_Help_Yes' : 'Alarm_Detail_Logs_Help_No');
  case AlarmLogEventKind.Protocol:
    return t(data.kind === 'help' ? 'Alarm_Detail_Logs_Protocol_Help' : 'Alarm_Detail_Logs_Protocol_Verification');
  case AlarmLogEventKind.Acknowledge:
    return t('Alarm_Detail_Logs_Acknowledge');
  case AlarmLogEventKind.AlarmMerged:
    return t('Alarm_Detail_Logs_Merged');
  case AlarmLogEventKind.AlarmRepublished:
    return t('Alarm_Detail_Logs_Republished');
  case AlarmLogEventKind.SeverityIncreased:
    return t('Alarm_Detail_Logs_Severity');
  case AlarmLogEventKind.CallMeta:
    return t('Alarm_Detail_Logs_CallMeta');
  case AlarmLogEventKind.ProfessionalResponder:
    if (data.details.kind === AlarmLogProfessionalResponderKind.Note) {

      return t('Alarm_Detail_Logs_Responder_Note', { name: data.responderName });

    }
    return t('Alarm_Detail_Logs_Responder', { name: data.responderName, kind: data.details.kind });
  default:
    return humanize((data as { eventType: string }).eventType);

  }

};

/** A call that ended without the person taking it: no answer, not reachable, or turned down. */
export const isCallUnanswered = (event: AlarmLogEvent): boolean => {

  const { data } = event;

  return (data?.eventType === AlarmLogEventKind.ActionCall
    || data?.eventType === AlarmLogEventKind.ActionCallStaticResponder
    || data?.eventType === AlarmLogEventKind.ActionCallToStaticResponder
    || data?.eventType === AlarmLogEventKind.ActionCallToDynamicResponder)
    && [AlarmLogActionCallKind.ResultCancelled, AlarmLogActionCallKind.ResultNotReachable, AlarmLogActionCallKind.ResultNoResponse].includes(data.kind);

};

/** The short line of an outreach action inside the summon card; the card already names the person. */
export const alarmLogSummonTitle = (event: AlarmLogEvent, t: TranslationFunc): string => {

  const { data } = event;

  switch (data?.eventType) {

  case AlarmLogEventKind.Notification:
    return t('Alarm_Detail_Logs_Summon_Push');
  case AlarmLogEventKind.Sms:
    return t('Alarm_Detail_Logs_Summon_Sms');
  case AlarmLogEventKind.Email:
    return t('Alarm_Detail_Logs_Summon_Email');
  case AlarmLogEventKind.ActionCall:
  case AlarmLogEventKind.ActionCallStaticResponder:
  case AlarmLogEventKind.ActionCallToStaticResponder:
  case AlarmLogEventKind.ActionCallToDynamicResponder:
    return t(`Alarm_Detail_Logs_Summon_Call_${actionCallKindKey(data.kind)}`);
  default:
    return alarmLogTitle(event, t);

  }

};

export const alarmLogDetail = (event: AlarmLogEvent, t: TranslationFunc): string | undefined => {

  const { data } = event;

  if (!data) return undefined;

  if (data.eventType === AlarmLogEventKind.Acknowledge) {

    const lines = [
      data.comment,
      data.survey && t('Alarm_Detail_Logs_AckSurvey', { text: data.survey }),
      data.systemAck && t('Alarm_Detail_Logs_AckSystem', { reason: data.systemAck }),
    ].filter(Boolean);

    return lines.length > 0 ? lines.join('\n') : undefined;

  }

  if (data.eventType === AlarmLogEventKind.Chat) return isEscalatedToAlarmCenter(data) ? undefined : data.message;

  if (data.eventType === AlarmLogEventKind.ResponseConfirmation && data.revision) return t('Alarm_Detail_Logs_Help_Revised');

  if (data.eventType === AlarmLogEventKind.CallSession) {

    return t(data.callType === AlarmCallType.AlarmCall ? 'Alarm_Detail_Logs_CallType_alarmCall' : 'Alarm_Detail_Logs_CallType_helperCall');

  }

  if (
    data.eventType === AlarmLogEventKind.ProfessionalResponder
    && data.details.kind === AlarmLogProfessionalResponderKind.Note
  ) {

    return data.details.note;

  }

  return undefined;

};
